import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { gstr3bTiles, financialYears, months, generateARN } from '../data/mockData';
import GSTSIAAssistant from '../components/gst/GSTSIAAssistant';
import { 
  FileSpreadsheet, ArrowLeft, Save, Eye, Send, CheckCircle2, AlertCircle, 
  Calendar, Edit3, ChevronRight, Calculator, IndianRupee, CreditCard, Receipt
} from 'lucide-react';

// Default company data
const DEFAULT_COMPANY = {
  legalName: 'Demo Company Private Limited',
  tradeName: 'Demo Company',
  gstin: '07AAACG1234A1ZM'
};

const GSTR3BDashboard = ({ skipAuth = false, companyData: propCompanyData = null }) => {
  const navigate = useNavigate();
  const { isLoggedIn, isAdmin, isGstr3bPaid, loading } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState({ year: '2024-25', month: 'July' });
  const [selectedSection, setSelectedSection] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [generatedARN, setGeneratedARN] = useState('');
  const [companyData, setCompanyData] = useState(propCompanyData || DEFAULT_COMPANY);

  // Load company data from localStorage if not provided via props
  useEffect(() => {
    if (!propCompanyData) {
      const savedData = localStorage.getItem('practiq_gst_company');
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          setCompanyData({ ...DEFAULT_COMPANY, ...parsed });
        } catch (e) {
          console.error('Error loading company data:', e);
        }
      }
    }
  }, [propCompanyData]);
  
  // Check if user has access (skipAuth from simulator bypasses auth)
  const hasAccess = skipAuth || isLoggedIn || isAdmin || isGstr3bPaid;
  // Form data for all sections
  const [formData, setFormData] = useState({
    '3.1': {
      a: { taxableValue: '', igst: '', cgst: '', sgst: '', cess: '' },
      b: { taxableValue: '', igst: '', cgst: '', sgst: '', cess: '' },
      c: { taxableValue: '', igst: '', cgst: '', sgst: '', cess: '' },
      d: { taxableValue: '', igst: '', cgst: '', sgst: '', cess: '' },
      e: { taxableValue: '', igst: '', cgst: '', sgst: '', cess: '' }
    },
    '3.2': {
      unreg: { place: '', taxableValue: '', igst: '' },
      comp: { place: '', taxableValue: '', igst: '' },
      uin: { place: '', taxableValue: '', igst: '' }
    },
    '4': {
      A: {
        '1': { igst: '', cgst: '', sgst: '', cess: '' },
        '2': { igst: '', cgst: '', sgst: '', cess: '' },
        '3': { igst: '', cgst: '', sgst: '', cess: '' },
        '4': { igst: '', cgst: '', sgst: '', cess: '' },
        '5': { igst: '', cgst: '', sgst: '', cess: '' }
      },
      B: {
        '1': { igst: '', cgst: '', sgst: '', cess: '' },
        '2': { igst: '', cgst: '', sgst: '', cess: '' }
      },
      C: { igst: '', cgst: '', sgst: '', cess: '' },
      D: {
        '1': { igst: '', cgst: '', sgst: '', cess: '' },
        '2': { igst: '', cgst: '', sgst: '', cess: '' }
      }
    },
    '5': {
      inter: { fromReg: '', fromUnreg: '' },
      intra: { fromReg: '', fromUnreg: '' }
    },
    '5.1': {
      interest: { igst: '', cgst: '', sgst: '', cess: '' },
      latefee: { cgst: '', sgst: '' }
    },
    '6.1': {
      igst: { taxPayable: '', paidCash: '', paidITC: '' },
      cgst: { taxPayable: '', paidCash: '', paidITC: '' },
      sgst: { taxPayable: '', paidCash: '', paidITC: '' },
      cess: { taxPayable: '', paidCash: '', paidITC: '' }
    }
  });

  useEffect(() => {
    // Wait for auth to load before checking access (skip if coming from simulator)
    if (!skipAuth && !loading && !hasAccess) {
      navigate('/portal/login');
    }
  }, [loading, hasAccess, navigate, skipAuth]);

  const handleFileReturn = () => {
    const arn = generateARN();
    setGeneratedARN(arn);
    setShowSuccessModal(true);
  };

  const getTotalOutwardTax = () => {
    const section = formData['3.1'];
    let total = 0;
    Object.values(section).forEach(row => {
      total += parseFloat(row.igst || 0) + parseFloat(row.cgst || 0) + parseFloat(row.sgst || 0);
    });
    return total.toFixed(2);
  };

  const getTotalITC = () => {
    const section = formData['4'];
    let total = 0;
    if (section.A) {
      Object.values(section.A).forEach(row => {
        total += parseFloat(row.igst || 0) + parseFloat(row.cgst || 0) + parseFloat(row.sgst || 0);
      });
    }
    return total.toFixed(2);
  };

  const updateFormData = (section, key, subKey, field, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      if (subKey) {
        newData[section][key][subKey] = { ...newData[section][key][subKey], [field]: value };
      } else {
        newData[section][key] = { ...newData[section][key], [field]: value };
      }
      return newData;
    });
  };

  const tiles = [
    { id: '3.1', title: '3.1 Outward Supplies', description: 'Tax on outward and reverse charge inward supplies', icon: Receipt, color: 'bg-blue-500' },
    { id: '3.2', title: '3.2 Inter-State Supplies', description: 'Supplies to unregistered, composition & UIN holders', icon: IndianRupee, color: 'bg-green-500' },
    { id: '4', title: '4. Eligible ITC', description: 'Input Tax Credit available and reversed', icon: CreditCard, color: 'bg-purple-500' },
    { id: '5', title: '5. Exempt Supplies', description: 'Exempt, nil-rated and non-GST inward supplies', icon: FileSpreadsheet, color: 'bg-amber-500' },
    { id: '5.1', title: '5.1 Interest & Late Fee', description: 'Interest and late fee payable', icon: Calculator, color: 'bg-red-500' },
    { id: '6.1', title: '6.1 Payment of Tax', description: 'Tax payable and payment details', icon: IndianRupee, color: 'bg-teal-500' }
  ];

  // Show loading state while checking auth
  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!hasAccess) return null;

  return (
    <Layout>
      {/* Page Header */}
      <div className="bg-[#0D1B2A] text-white py-6">
        <div className="w-full px-6 lg:px-12">
          {/* Back Button + Breadcrumb */}
          <div className="flex items-center gap-4 mb-3">
            <button 
              onClick={() => navigate('/gst')}
              className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to GST Dashboard
            </button>
            <div className="flex items-center gap-2 text-blue-200 text-sm">
              <span className="cursor-pointer hover:text-white" onClick={() => navigate('/')}>Home</span>
              <span>/</span>
              <span className="cursor-pointer hover:text-white" onClick={() => navigate('/gst')}>GST Services</span>
              <span>/</span>
              <span>GSTR-3B</span>
            </div>
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">GSTR-3B - Monthly Summary Return</h1>
              <p className="text-blue-200 mt-1">Self-declaration of tax liability and ITC claimed</p>
            </div>
            <Badge className="bg-green-500 text-white w-fit">
              GSTIN: {companyData.gstin}
            </Badge>
          </div>
        </div>
      </div>

      {/* Company Info Banner */}
      {skipAuth && (
        <div className="bg-blue-50 border-b border-blue-200">
          <div className="w-full px-6 lg:px-12 py-2">
            <p className="text-sm text-blue-800">
              Filing for: <span className="font-semibold">{companyData.legalName}</span>
              {companyData.tradeName && companyData.tradeName !== companyData.legalName && (
                <span className="text-blue-600 ml-2">({companyData.tradeName})</span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Period Selector */}
      <div className="bg-white border-b shadow-sm">
        <div className="w-full px-6 lg:px-12 py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Return Period:</span>
            </div>
            <Select value={selectedPeriod.year} onValueChange={(value) => setSelectedPeriod(prev => ({ ...prev, year: value }))}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {financialYears.map(fy => (
                  <SelectItem key={fy} value={fy}>{fy}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedPeriod.month} onValueChange={(value) => setSelectedPeriod(prev => ({ ...prev, month: value }))}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map(month => (
                  <SelectItem key={month} value={month}>{month}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Badge variant="outline" className="ml-auto">
              Due Date: 20th of following month
            </Badge>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-8 bg-gray-50">
        <div className="w-full px-6 lg:px-12">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <p className="text-sm text-blue-600 mb-1">Total Outward Tax</p>
                <p className="text-2xl font-bold text-blue-800">₹{getTotalOutwardTax()}</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <p className="text-sm text-green-600 mb-1">Total ITC Available</p>
                <p className="text-2xl font-bold text-green-800">₹{getTotalITC()}</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <p className="text-sm text-amber-600 mb-1">Return Status</p>
                <p className="text-lg font-semibold text-amber-800">Draft</p>
              </CardContent>
            </Card>
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-4">
                <p className="text-sm text-purple-600 mb-1">Period</p>
                <p className="text-lg font-semibold text-purple-800">{selectedPeriod.month} {selectedPeriod.year}</p>
              </CardContent>
            </Card>
          </div>

          {selectedSection ? (
            /* Section Detail View */
            <Card>
              <CardHeader className="border-b bg-gray-50">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedSection(null)}>
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  <div>
                    <CardTitle className="text-[#0D1B2A]">{selectedSection.title}</CardTitle>
                    <CardDescription>{selectedSection.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {selectedSection.id === '3.1' && (
                  <GSTR3BSection31 formData={formData['3.1']} updateFormData={updateFormData} />
                )}
                {selectedSection.id === '3.2' && (
                  <GSTR3BSection32 formData={formData['3.2']} updateFormData={updateFormData} />
                )}
                {selectedSection.id === '4' && (
                  <GSTR3BSection4 formData={formData['4']} updateFormData={updateFormData} />
                )}
                {selectedSection.id === '5' && (
                  <GSTR3BSection5 formData={formData['5']} updateFormData={updateFormData} />
                )}
                {selectedSection.id === '5.1' && (
                  <GSTR3BSection51 formData={formData['5.1']} updateFormData={updateFormData} />
                )}
                {selectedSection.id === '6.1' && (
                  <GSTR3BSection61 formData={formData['6.1']} updateFormData={updateFormData} />
                )}
              </CardContent>
            </Card>
          ) : (
            /* Tiles Grid */
            <>
              <h2 className="text-lg font-semibold text-[#0D1B2A] mb-4">Select a section to enter details</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tiles.map((tile) => (
                  <Card 
                    key={tile.id}
                    className="hover:shadow-lg transition-all duration-300 cursor-pointer group border-l-4 border-l-transparent hover:border-l-[#0D1B2A]"
                    onClick={() => setSelectedSection(tile)}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-12 h-12 ${tile.color} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform`}>
                          <tile.icon className="w-6 h-6 text-white" />
                        </div>
                        <Badge variant="outline" className="text-xs">Click to edit</Badge>
                      </div>
                      <h3 className="font-semibold text-[#0D1B2A] mb-1 group-hover:text-blue-700 transition-colors">
                        {tile.title}
                      </h3>
                      <p className="text-sm text-gray-500">{tile.description}</p>
                      <div className="mt-4 flex items-center text-sm text-blue-600 group-hover:text-blue-700">
                        <span>Enter Details</span>
                        <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div className="mt-8 flex flex-wrap gap-4 justify-end">
            <Button variant="outline" className="min-w-[120px]">
              <Save className="w-4 h-4 mr-2" />
              Save Draft
            </Button>
            <Button variant="outline" className="min-w-[120px]">
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button 
              onClick={handleFileReturn}
              className="min-w-[180px] bg-green-600 hover:bg-green-700"
            >
              <Send className="w-4 h-4 mr-2" />
              File Return with EVC/DSC
            </Button>
          </div>
        </div>
      </section>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md">
          <div className="text-center py-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <DialogTitle className="text-2xl text-green-700 mb-2">
              Return Filed Successfully!
            </DialogTitle>
            <DialogDescription className="text-gray-600 mb-6">
              Your GSTR-3B has been filed successfully.
            </DialogDescription>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-500 mb-2">Acknowledgment Reference Number (ARN)</p>
              <p className="text-2xl font-mono font-bold text-[#0D1B2A]">{generatedARN}</p>
            </div>

            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 mb-6 py-2 px-4">
              Created via PractiQ by CA Shivangi Mittal
            </Badge>

            <Button
              onClick={() => {
                setShowSuccessModal(false);
                navigate('/gst');
              }}
              className="w-full bg-[#0D1B2A] hover:bg-[#1B2838]"
            >
              Back to Dashboard
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* SIA Assistant */}
      <GSTSIAAssistant formType="GSTR-3B" />
    </Layout>
  );
};

// Section 3.1 Component
const GSTR3BSection31 = ({ formData, updateFormData }) => {
  const rows = [
    { key: 'a', label: '(a) Outward taxable supplies (other than zero rated, nil rated and exempted)' },
    { key: 'b', label: '(b) Outward taxable supplies (zero rated)' },
    { key: 'c', label: '(c) Other outward supplies (Nil rated, exempted)' },
    { key: 'd', label: '(d) Inward supplies (liable to reverse charge)' },
    { key: 'e', label: '(e) Non-GST outward supplies' }
  ];

  return (
    <div className="space-y-6">
      <Alert className="bg-blue-50 border-blue-200">
        <AlertCircle className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          Enter details of outward supplies and tax liability. All amounts should be in rupees.
        </AlertDescription>
      </Alert>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="min-w-[300px]">Nature of Supplies</TableHead>
              <TableHead className="text-right">Taxable Value</TableHead>
              <TableHead className="text-right">IGST</TableHead>
              <TableHead className="text-right">CGST</TableHead>
              <TableHead className="text-right">SGST/UTGST</TableHead>
              <TableHead className="text-right">CESS</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell className="font-medium text-sm">{row.label}</TableCell>
                <TableCell>
                  <Input
                    type="number"
                    value={formData[row.key]?.taxableValue || ''}
                    onChange={(e) => updateFormData('3.1', row.key, null, 'taxableValue', e.target.value)}
                    className="text-right w-28"
                    placeholder="0.00"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    value={formData[row.key]?.igst || ''}
                    onChange={(e) => updateFormData('3.1', row.key, null, 'igst', e.target.value)}
                    className="text-right w-24"
                    placeholder="0.00"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    value={formData[row.key]?.cgst || ''}
                    onChange={(e) => updateFormData('3.1', row.key, null, 'cgst', e.target.value)}
                    className="text-right w-24"
                    placeholder="0.00"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    value={formData[row.key]?.sgst || ''}
                    onChange={(e) => updateFormData('3.1', row.key, null, 'sgst', e.target.value)}
                    className="text-right w-24"
                    placeholder="0.00"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    value={formData[row.key]?.cess || ''}
                    onChange={(e) => updateFormData('3.1', row.key, null, 'cess', e.target.value)}
                    className="text-right w-24"
                    placeholder="0.00"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// Section 3.2 Component
const GSTR3BSection32 = ({ formData, updateFormData }) => {
  const rows = [
    { key: 'unreg', label: 'Supplies made to Unregistered Persons' },
    { key: 'comp', label: 'Supplies made to Composition Taxable Persons' },
    { key: 'uin', label: 'Supplies made to UIN holders' }
  ];

  return (
    <div className="space-y-6">
      <Alert className="bg-blue-50 border-blue-200">
        <AlertCircle className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          Enter details of inter-state supplies made to specific categories.
        </AlertDescription>
      </Alert>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="min-w-[250px]">Nature of Supplies</TableHead>
              <TableHead className="text-right">Place of Supply (State Code)</TableHead>
              <TableHead className="text-right">Taxable Value</TableHead>
              <TableHead className="text-right">IGST</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell className="font-medium text-sm">{row.label}</TableCell>
                <TableCell>
                  <Input
                    value={formData[row.key]?.place || ''}
                    onChange={(e) => updateFormData('3.2', row.key, null, 'place', e.target.value)}
                    className="text-right w-24"
                    placeholder="e.g., 07"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    value={formData[row.key]?.taxableValue || ''}
                    onChange={(e) => updateFormData('3.2', row.key, null, 'taxableValue', e.target.value)}
                    className="text-right w-28"
                    placeholder="0.00"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    value={formData[row.key]?.igst || ''}
                    onChange={(e) => updateFormData('3.2', row.key, null, 'igst', e.target.value)}
                    className="text-right w-24"
                    placeholder="0.00"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// Section 4 (ITC) Component
const GSTR3BSection4 = ({ formData, updateFormData }) => {
  return (
    <div className="space-y-6">
      <Alert className="bg-blue-50 border-blue-200">
        <AlertCircle className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          Enter Input Tax Credit details. Net ITC is auto-calculated as (A) - (B).
        </AlertDescription>
      </Alert>
      
      <Accordion type="multiple" className="space-y-4">
        <AccordionItem value="a" className="border rounded-lg">
          <AccordionTrigger className="px-4 hover:no-underline hover:bg-gray-50">
            <span className="font-semibold text-[#0D1B2A]">(A) ITC Available (whether in full or part)</span>
          </AccordionTrigger>
          <AccordionContent className="p-4">
            <div className="space-y-4">
              {[
                { key: '1', label: '(1) Import of goods' },
                { key: '2', label: '(2) Import of services' },
                { key: '3', label: '(3) Inward supplies liable to reverse charge' },
                { key: '4', label: '(4) Inward supplies from ISD' },
                { key: '5', label: '(5) All other ITC' }
              ].map((row) => (
                <div key={row.key} className="grid grid-cols-5 gap-2 items-center">
                  <span className="text-sm col-span-1">{row.label}</span>
                  <Input
                    type="number"
                    value={formData.A?.[row.key]?.igst || ''}
                    onChange={(e) => updateFormData('4', 'A', row.key, 'igst', e.target.value)}
                    placeholder="IGST"
                    className="text-right"
                  />
                  <Input
                    type="number"
                    value={formData.A?.[row.key]?.cgst || ''}
                    onChange={(e) => updateFormData('4', 'A', row.key, 'cgst', e.target.value)}
                    placeholder="CGST"
                    className="text-right"
                  />
                  <Input
                    type="number"
                    value={formData.A?.[row.key]?.sgst || ''}
                    onChange={(e) => updateFormData('4', 'A', row.key, 'sgst', e.target.value)}
                    placeholder="SGST"
                    className="text-right"
                  />
                  <Input
                    type="number"
                    value={formData.A?.[row.key]?.cess || ''}
                    onChange={(e) => updateFormData('4', 'A', row.key, 'cess', e.target.value)}
                    placeholder="CESS"
                    className="text-right"
                  />
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="b" className="border rounded-lg">
          <AccordionTrigger className="px-4 hover:no-underline hover:bg-gray-50">
            <span className="font-semibold text-[#0D1B2A]">(B) ITC Reversed</span>
          </AccordionTrigger>
          <AccordionContent className="p-4">
            <div className="space-y-4">
              {[
                { key: '1', label: '(1) As per rules 42 & 43 of CGST Rules' },
                { key: '2', label: '(2) Others' }
              ].map((row) => (
                <div key={row.key} className="grid grid-cols-5 gap-2 items-center">
                  <span className="text-sm col-span-1">{row.label}</span>
                  <Input
                    type="number"
                    value={formData.B?.[row.key]?.igst || ''}
                    onChange={(e) => updateFormData('4', 'B', row.key, 'igst', e.target.value)}
                    placeholder="IGST"
                    className="text-right"
                  />
                  <Input
                    type="number"
                    value={formData.B?.[row.key]?.cgst || ''}
                    onChange={(e) => updateFormData('4', 'B', row.key, 'cgst', e.target.value)}
                    placeholder="CGST"
                    className="text-right"
                  />
                  <Input
                    type="number"
                    value={formData.B?.[row.key]?.sgst || ''}
                    onChange={(e) => updateFormData('4', 'B', row.key, 'sgst', e.target.value)}
                    placeholder="SGST"
                    className="text-right"
                  />
                  <Input
                    type="number"
                    value={formData.B?.[row.key]?.cess || ''}
                    onChange={(e) => updateFormData('4', 'B', row.key, 'cess', e.target.value)}
                    placeholder="CESS"
                    className="text-right"
                  />
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

// Section 5 Component
const GSTR3BSection5 = ({ formData, updateFormData }) => {
  return (
    <div className="space-y-6">
      <Alert className="bg-blue-50 border-blue-200">
        <AlertCircle className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          Enter values of exempt, nil-rated and non-GST inward supplies.
        </AlertDescription>
      </Alert>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead>Nature of Supply</TableHead>
              <TableHead className="text-right">From Registered Suppliers</TableHead>
              <TableHead className="text-right">From Unregistered Suppliers</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">Inter-State Supplies</TableCell>
              <TableCell>
                <Input
                  type="number"
                  value={formData.inter?.fromReg || ''}
                  onChange={(e) => updateFormData('5', 'inter', null, 'fromReg', e.target.value)}
                  className="text-right"
                  placeholder="0.00"
                />
              </TableCell>
              <TableCell>
                <Input
                  type="number"
                  value={formData.inter?.fromUnreg || ''}
                  onChange={(e) => updateFormData('5', 'inter', null, 'fromUnreg', e.target.value)}
                  className="text-right"
                  placeholder="0.00"
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Intra-State Supplies</TableCell>
              <TableCell>
                <Input
                  type="number"
                  value={formData.intra?.fromReg || ''}
                  onChange={(e) => updateFormData('5', 'intra', null, 'fromReg', e.target.value)}
                  className="text-right"
                  placeholder="0.00"
                />
              </TableCell>
              <TableCell>
                <Input
                  type="number"
                  value={formData.intra?.fromUnreg || ''}
                  onChange={(e) => updateFormData('5', 'intra', null, 'fromUnreg', e.target.value)}
                  className="text-right"
                  placeholder="0.00"
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// Section 5.1 Component
const GSTR3BSection51 = ({ formData, updateFormData }) => {
  return (
    <div className="space-y-6">
      <Alert className="bg-amber-50 border-amber-200">
        <AlertCircle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800">
          Interest is calculated @ 18% p.a. on the tax amount. Late fee is applicable for delayed filing.
        </AlertDescription>
      </Alert>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead>Description</TableHead>
              <TableHead className="text-right">IGST</TableHead>
              <TableHead className="text-right">CGST</TableHead>
              <TableHead className="text-right">SGST/UTGST</TableHead>
              <TableHead className="text-right">CESS</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">Interest</TableCell>
              <TableCell>
                <Input
                  type="number"
                  value={formData.interest?.igst || ''}
                  onChange={(e) => updateFormData('5.1', 'interest', null, 'igst', e.target.value)}
                  className="text-right"
                  placeholder="0.00"
                />
              </TableCell>
              <TableCell>
                <Input
                  type="number"
                  value={formData.interest?.cgst || ''}
                  onChange={(e) => updateFormData('5.1', 'interest', null, 'cgst', e.target.value)}
                  className="text-right"
                  placeholder="0.00"
                />
              </TableCell>
              <TableCell>
                <Input
                  type="number"
                  value={formData.interest?.sgst || ''}
                  onChange={(e) => updateFormData('5.1', 'interest', null, 'sgst', e.target.value)}
                  className="text-right"
                  placeholder="0.00"
                />
              </TableCell>
              <TableCell>
                <Input
                  type="number"
                  value={formData.interest?.cess || ''}
                  onChange={(e) => updateFormData('5.1', 'interest', null, 'cess', e.target.value)}
                  className="text-right"
                  placeholder="0.00"
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Late Fee</TableCell>
              <TableCell className="bg-gray-50">-</TableCell>
              <TableCell>
                <Input
                  type="number"
                  value={formData.latefee?.cgst || ''}
                  onChange={(e) => updateFormData('5.1', 'latefee', null, 'cgst', e.target.value)}
                  className="text-right"
                  placeholder="0.00"
                />
              </TableCell>
              <TableCell>
                <Input
                  type="number"
                  value={formData.latefee?.sgst || ''}
                  onChange={(e) => updateFormData('5.1', 'latefee', null, 'sgst', e.target.value)}
                  className="text-right"
                  placeholder="0.00"
                />
              </TableCell>
              <TableCell className="bg-gray-50">-</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// Section 6.1 Component
const GSTR3BSection61 = ({ formData, updateFormData }) => {
  return (
    <div className="space-y-6">
      <Alert className="bg-green-50 border-green-200">
        <AlertCircle className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-green-800">
          Enter tax payable and payment details. Payment can be made through Cash Ledger or ITC utilization.
        </AlertDescription>
      </Alert>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Tax Payable</TableHead>
              <TableHead className="text-right">Paid through ITC</TableHead>
              <TableHead className="text-right">Paid in Cash</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[
              { key: 'igst', label: 'Integrated Tax' },
              { key: 'cgst', label: 'Central Tax' },
              { key: 'sgst', label: 'State/UT Tax' },
              { key: 'cess', label: 'CESS' }
            ].map((row) => (
              <TableRow key={row.key}>
                <TableCell className="font-medium">{row.label}</TableCell>
                <TableCell>
                  <Input
                    type="number"
                    value={formData[row.key]?.taxPayable || ''}
                    onChange={(e) => updateFormData('6.1', row.key, null, 'taxPayable', e.target.value)}
                    className="text-right"
                    placeholder="0.00"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    value={formData[row.key]?.paidITC || ''}
                    onChange={(e) => updateFormData('6.1', row.key, null, 'paidITC', e.target.value)}
                    className="text-right"
                    placeholder="0.00"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    value={formData[row.key]?.paidCash || ''}
                    onChange={(e) => updateFormData('6.1', row.key, null, 'paidCash', e.target.value)}
                    className="text-right"
                    placeholder="0.00"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default GSTR3BDashboard;
